import { createContext, useContext, useState } from 'react';
import { APIContext } from './APIContext';
import SearchFilterViewModel from '../Management/Organisations/ViewModels/SearchFilterViewmodel';
import OrganisationTableViewModel from '../Management/Organisations/ViewModels/OrganisationTableViewModel';

type ContextModel = {
    saveFilters: (saveName?: string, callback?: (auditCode: string) => void) => void;
    loadFilters: (saveId: string) => void;
    filterState: SearchFilterViewModel;
    updateFilterState: (properties: Partial<SearchFilterViewModel>, callback?: (newState?: SearchFilterViewModel) => void) => void;
    searchResults: OrganisationTableViewModel;
    updateResultsState: (properties: Partial<OrganisationTableViewModel>, callback?: (newState?: OrganisationTableViewModel) => void) => void;
    updateNextOrganisation: (currentId: string) => void;
    nextOrganisationResult: string | null;
};

export const OrganisationsContext = createContext<ContextModel>({
    saveFilters: () => { throw Error('Cannot use a context without a provider'); },
    loadFilters: () => { throw Error('Cannot use a context without a provider'); },
    filterState: new SearchFilterViewModel(),
    updateFilterState: () => { throw Error('Cannot use a context without a provider'); },
    searchResults: new OrganisationTableViewModel(),
    updateResultsState: () => { throw Error('Cannot use a context without a provider'); },
    updateNextOrganisation: () => { throw Error('Cannot use a context without a provider'); },
    nextOrganisationResult: null
});

export default function OrganisationsContextProvider({ children }: React.PropsWithChildren<{}>) {
    const { post, get } = useContext(APIContext);
    const [filterState, setFilterState] = useState<SearchFilterViewModel>(new SearchFilterViewModel());
    const [searchResults, setSearchResults] = useState<OrganisationTableViewModel>(new OrganisationTableViewModel());
    const [nextOrganisationResult, setNextOrgResult] = useState<string | null>(null);

    const saveFilters = async (saveName?: string) => {
        const savedFilters = {
            state: JSON.stringify(filterState),
            name: saveName ?? `OrganisationSearch_${new Date().toISOString()}`,
        };
        post('OrganisationFilter', 'SaveFilters', savedFilters);
    }

    const loadFilters = async (saveId: string) => {
        get('OrganisationFilter', `LoadFilters?sessionId=${saveId}`, (data: SearchFilterViewModel) => {
            setFilterState(data);
        });
    }

    const updateFilterState = async (properties: Partial<SearchFilterViewModel>, callback?: (newState?: SearchFilterViewModel) => void) => {
        setFilterState(prev => {
            const newState = { ...prev, ...properties };
            if (callback) {
                callback(newState);
            }
            return newState;
        });
    }

    const updateResultsState = async (properties: Partial<OrganisationTableViewModel>, callback?: (newState?: OrganisationTableViewModel) => void) => {
        setSearchResults(prev => {
            const newState = { ...prev, ...properties };
            if (callback) {
                callback(newState);
            }
            return newState;
        });
    }

    const updateNextOrganisation = (currentId: string) => {
        const index = searchResults.rows.findIndex(f => f.id === currentId);
        const lastPage = Math.ceil(searchResults.count / filterState.rowsPerPage);

        if (index > -1) {
            // return null if viewing last result of last page
            if (filterState.pageNumber == lastPage && index == searchResults.rows.length - 1) {
                setNextOrgResult(null);
                return;
            }

            // return next id if on current page results, else update results to next page and get index
            if (index < searchResults.rows.length - 1) {
                setNextOrgResult(searchResults.rows[index + 1].id);
                return;
            }

            updateFilterState({ pageNumber: filterState.pageNumber + 1 }, (newFilters) => {
                post("Organisation", `GetOrganisationTable?order=${newFilters!.order}&orderBy=${newFilters!.orderByProp}`, newFilters, (data) => {
                    updateResultsState(data);
                    setNextOrgResult(data.rows[0].id);
                });
            });
        }

        return;
    }

    const context: ContextModel = {
        saveFilters,
        loadFilters,
        filterState,
        updateFilterState,
        searchResults,
        updateResultsState,
        updateNextOrganisation,
        nextOrganisationResult
    }

    return (
        <OrganisationsContext.Provider value={context}>
            {children}
        </OrganisationsContext.Provider>
    );
}
